import Notifier from '@components/common/Notifier';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {highlightMatch} from '@account/utils/highlight-match';
import {useCustomerAccountAutosuggest} from '@hooks/data/ims/useCustomerAccountAutosuggest';
import {useDebounce} from 'use-debounce';

const AutocompleteOption = ({customerAccountId, name, ...rest}) => (
  <Stack
    {...rest}
    spacing={0.5}
    sx={theme => ({
      alignItems: 'start',
      ':not(:first-of-type)': {
        borderTop: 'solid 1px',
        borderTopColor: theme.palette.grey[300],
      },
      textWrap: 'wrap',
      wordBreak: 'break-word',
    })}
  >
    <Typography alignSelf="start">{name}</Typography>

    <Typography
      variant="caption"
      color="text.secondary"
      alignSelf="start"
      letterSpacing={0}
    >
      {customerAccountId}
    </Typography>
  </Stack>
);

AutocompleteOption.propTypes = {
  name: PropTypes.array,
  customerAccountId: PropTypes.array,
  props: PropTypes.object,
};

// A 403 is the only failure that actually means "you don't have access". The
// autosuggest endpoint is newer than some deployed pda-admin builds, so a 404
// here is version skew, not permissions - saying otherwise sends an operator
// to #sh-support for what is really a redeploy.
const errorMessage = error => {
  const status = error?.response?.status;

  if (status === 403) {
    return "Error loading customer accounts: It seems like you don't have access. Please reach out to #sh-support.";
  }

  // Name the status so a 404 (version skew) and a 502 (backend down) are
  // distinguishable in a screenshot - one is a redeploy, the other is an
  // incident, and a single generic string made an operator guess which.
  return `Error loading customer accounts (${status ?? 'network error'}). Please try again, or reach out to #sh-support if it keeps happening.`;
};

const CustomerAccountAutocomplete = ({
  id = 'account-autocomplete',
  limit = 8,
  onChange,
  placeholder = 'Search',
  status,
  sx,
  value,
  ...rest
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm.toLowerCase(), 300);

  const {isFetching, isLoading, data, error} = useCustomerAccountAutosuggest(
    {limit, search: debouncedSearchTerm, status},
    {
      enabled: !!debouncedSearchTerm,
      staleTime: 1000 * 60,
      keepPreviousData: false,
      // react-query retries 3x by default. A 404 during the pda-admin version
      // skew window is not transient, and every keystroke is its own query -
      // retrying turns one dead request into four.
      retry: false,
    }
  );

  // Gate the options on the live search term rather than evicting the cache on
  // close. The old removeQueries() ran while the debounced term was still
  // populated, so it re-enabled the query and refetched the entry it had just
  // dropped - a wasted request per close - and its prefix match also cleared
  // sibling pickers mounted on the same page. Reading [] until the user types
  // again gets the same "no stale options on reopen" behaviour for free.
  const autocompleteOptions = searchTerm ? (data?.results ?? []) : [];

  return (
    <>
      <Autocomplete
        disablePortal
        filterOptions={x => x} // needed because we're filtering on the server
        getOptionLabel={option => option.name}
        id={id}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        ListboxProps={{sx: {paddingBottom: 0, paddingTop: 0}}}
        loading={!!searchTerm && (isFetching || isLoading)}
        name="accountAutocomplete"
        noOptionsText={
          error
            ? 'Could not load accounts'
            : searchTerm
              ? 'No accounts found'
              : 'Start typing to search for an account...'
        }
        onChange={(_, account) => onChange(account)}
        onClose={() => setSearchTerm('')}
        options={autocompleteOptions}
        renderInput={params => (
          <TextField
            {...params}
            InputProps={{
              ...(params.InputProps ?? {}),
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={event => setSearchTerm(event.target.value)}
            placeholder={placeholder}
            value={searchTerm}
          />
        )}
        renderOption={({key, ...props}, option) => (
          <AutocompleteOption
            key={option.id}
            name={highlightMatch(debouncedSearchTerm, option.name)}
            // Highlight the ID too - on a pasted UUID the term never occurs in
            // the name, so otherwise the row that matched gives no clue why.
            customerAccountId={highlightMatch(debouncedSearchTerm, option.id)}
            {...props}
          />
        )}
        size="small"
        slotProps={{paper: {elevation: 4}}}
        sx={sx}
        {...rest}
        value={value}
      />

      {error && (
        <Notifier message={errorMessage(error)} open severity="error" />
      )}
    </>
  );
};

CustomerAccountAutocomplete.propTypes = {
  id: PropTypes.string,
  limit: PropTypes.number,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  status: PropTypes.oneOf(['active', 'disabled', 'expired']),
  sx: PropTypes.object,
  value: PropTypes.object,
};

export default CustomerAccountAutocomplete;
